import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';
import Modal from '../../containers/modal.jsx';
import Box from '../box/box.jsx';
import {deleteProject, fetchProject, listProjects, updateProject} from '../../lib/auth-api.js';
import {describeAuthError} from '../../lib/auth-error-messages.js';
import {base64ToUint8Array, isDecodableProject} from '../../lib/project-encoding.js';
import styles from './my-projects.css';

/*
 * "我的作品": browse the projects saved in the account, open one, rename it, or
 * delete it.
 *
 * Saving deliberately does not live here: the menu bar has a dedicated save
 * button that asks for the name before uploading, so this dialog never
 * overwrites project content. Renaming edits the stored name only and never
 * touches the .sb3 payload.
 *
 * Projects are kept on the authentication service rather than in this browser,
 * so signing in on another machine shows the same list. The stored payload is
 * the same .sb3 format the "download to computer" action produces.
 */
const messages = defineMessages({
    title: {
        id: 'gui.myProjects.title',
        defaultMessage: '我的作品',
        description: 'Title of the saved projects dialog'
    },
    savedList: {
        id: 'gui.myProjects.savedList',
        defaultMessage: '已保存的作品',
        description: 'Heading above the list of saved projects'
    },
    loading: {
        id: 'gui.myProjects.loading',
        defaultMessage: '正在加载…',
        description: 'Shown while the project list is loading'
    },
    empty: {
        id: 'gui.myProjects.empty',
        defaultMessage: '还没有保存过作品。点击菜单栏的“保存”按钮即可把当前作品存到你的账号。',
        description: 'Shown when the account has no saved projects'
    },
    open: {
        id: 'gui.myProjects.open',
        defaultMessage: '打开',
        description: 'Button that opens a saved project'
    },
    rename: {
        id: 'gui.myProjects.rename',
        defaultMessage: '重命名',
        description: 'Button that starts renaming a saved project'
    },
    renameConfirm: {
        id: 'gui.myProjects.renameConfirm',
        defaultMessage: '保存',
        description: 'Button that applies a new project name'
    },
    renameCancel: {
        id: 'gui.myProjects.renameCancel',
        defaultMessage: '取消',
        description: 'Button that abandons a rename'
    },
    renamePlaceholder: {
        id: 'gui.myProjects.renamePlaceholder',
        defaultMessage: '作品名称',
        description: 'Placeholder of the rename field'
    },
    renameRequired: {
        id: 'gui.myProjects.renameRequired',
        defaultMessage: '作品名称不能为空。',
        description: 'Error shown when a rename is submitted with an empty name'
    },
    renamed: {
        id: 'gui.myProjects.renamed',
        defaultMessage: '已重命名为《{name}》。',
        description: 'Confirmation after renaming a project'
    },
    remove: {
        id: 'gui.myProjects.remove',
        defaultMessage: '删除',
        description: 'Button that deletes a saved project'
    },
    confirmRemove: {
        id: 'gui.myProjects.confirmRemove',
        defaultMessage: '确定要删除《{name}》吗？此操作无法撤销。',
        description: 'Confirmation asked before deleting a saved project'
    },
    cancel: {
        id: 'gui.myProjects.cancel',
        defaultMessage: '取消',
        description: 'Button that cancels a pending deletion'
    },
    opened: {
        id: 'gui.myProjects.opened',
        defaultMessage: '已打开《{name}》。',
        description: 'Confirmation after opening a project'
    },
    removed: {
        id: 'gui.myProjects.removed',
        defaultMessage: '已删除《{name}》。',
        description: 'Confirmation after deleting a project'
    }
});

// Matches the limit the save dialog enforces, so a project cannot be renamed to
// something that could never have been saved in the first place.
const MAX_NAME_LENGTH = 100;

const formatBytes = size => {
    const bytes = Number(size) || 0;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// The service stores ISO-like timestamps in UTC; show them in local time.
const formatTime = value => {
    if (!value) return '';
    const parsed = new Date(`${String(value).replace(' ', 'T')}Z`);
    if (isNaN(parsed.getTime())) return String(value);
    const pad = number => String(number).padStart(2, '0');
    const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
    return `${date} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

class MyProjects extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            projects: [],
            isLoading: true,
            busyProjectId: null,
            pendingDelete: null,
            // The row currently being renamed, plus the text in its input.
            renamingId: null,
            renameValue: '',
            error: null,
            notice: null
        };
        bindAll(this, [
            'handleOpenProject',
            'handleAskDelete',
            'handleAskDeleteFor',
            'handleOpenProjectFor',
            'handleCancelDelete',
            'handleConfirmDelete',
            'handleStartRenameFor',
            'handleRenameChange',
            'handleRenameKeyDown',
            'handleConfirmRename',
            'handleCancelRename',
            'loadProjects'
        ]);
    }

    componentDidMount () {
        this.loadProjects();
    }

    loadProjects () {
        this.setState({isLoading: true, error: null});
        return listProjects()
            .then(projects => {
                this.setState({projects: projects, isLoading: false});
            })
            .catch(error => {
                this.setState({
                    isLoading: false,
                    error: this.props.intl.formatMessage(describeAuthError(error))
                });
            });
    }

    handleOpenProject (project) {
        this.setState({busyProjectId: project.id, error: null, notice: null});
        fetchProject(project.id)
            .then(full => {
                if (!full || !full.content) {
                    throw Object.assign(new Error('empty'), {code: 'PROJECT_NOT_FOUND'});
                }
                // Projects saved by the earlier text-based code lost their
                // binary bytes, so say so plainly instead of handing the VM a
                // broken archive (which silently produced an empty canvas).
                if (!isDecodableProject(full.content, full.encoding)) {
                    throw Object.assign(new Error('unreadable'), {
                        code: 'PROJECT_UNREADABLE'
                    });
                }
                // The VM loads .sb3 bytes, so turn the stored base64 back into
                // one. A project that mentions a device can make loadProject
                // reject *after* the project has already loaded; the example
                // library treats that as success too, and so does this dialog.
                // The VM detects the archive format from the input type: a Blob
                // is treated as project JSON text and fails while sniffing for
                // the legacy SB1 header, so hand it the raw bytes instead.
                return Promise.resolve(this.props.loadProject(
                    base64ToUint8Array(full.content).buffer
                ))
                    .catch(reason => {
                        // A device-related rejection arrives after the project
                        // has loaded, so it is not worth interrupting the user.
                        // A rejection that left the workspace empty is a real
                        // failure and must be shown, never swallowed.
                        if (this.props.didProjectLoad()) return;
                        throw Object.assign(new Error('open failed'), {
                            code: 'PROJECT_OPEN_FAILED',
                            cause: reason
                        });
                    });
            })
            .then(() => {
                // Point the menu bar save button at the project just opened, so
                // saving updates it instead of creating a second copy.
                this.props.onProjectOpened(project.id, project.name);
                this.setState({
                    busyProjectId: null,
                    notice: this.props.intl.formatMessage(messages.opened, {name: project.name})
                });
                this.props.onRequestClose();
            })
            .catch(error => {
                this.setState({
                    busyProjectId: null,
                    error: this.props.intl.formatMessage(describeAuthError(error))
                });
            });
    }

    handleStartRenameFor (project) {
        return () => this.setState({
            renamingId: project.id,
            renameValue: project.name,
            pendingDelete: null,
            error: null,
            notice: null
        });
    }

    handleRenameChange (event) {
        this.setState({renameValue: event.target.value, error: null});
    }

    handleRenameKeyDown (event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.handleConfirmRename();
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.handleCancelRename();
        }
    }

    handleCancelRename () {
        this.setState({renamingId: null, renameValue: '', error: null});
    }

    handleConfirmRename () {
        const project = this.state.projects.find(item => item.id === this.state.renamingId);
        if (!project) {
            this.handleCancelRename();
            return;
        }

        const name = this.state.renameValue.trim();
        if (!name) {
            this.setState({error: this.props.intl.formatMessage(messages.renameRequired)});
            return;
        }
        // Renaming to the same name is a no-op the user did not ask for.
        if (name === project.name) {
            this.handleCancelRename();
            return;
        }

        this.setState({busyProjectId: project.id, error: null, notice: null});
        // Only the name is sent, so the stored project content is left alone.
        updateProject(project.id, {name: name})
            .then(() => {
                // If the editor is linked to this project, the save dialog
                // pre-fills from the linked name and must follow the rename.
                this.props.onProjectRenamed(project.id, name);
                this.setState({
                    busyProjectId: null,
                    renamingId: null,
                    renameValue: '',
                    notice: this.props.intl.formatMessage(messages.renamed, {name: name})
                });
                return this.loadProjects();
            })
            .catch(error => {
                this.setState({
                    busyProjectId: null,
                    error: this.props.intl.formatMessage(describeAuthError(error))
                });
            });
    }

    handleAskDelete (project) {
        this.setState({pendingDelete: project, error: null, notice: null});
    }

    handleCancelDelete () {
        this.setState({pendingDelete: null});
    }

    handleConfirmDelete () {
        const project = this.state.pendingDelete;
        if (!project) return;
        this.setState({pendingDelete: null, busyProjectId: project.id, error: null, notice: null});
        deleteProject(project.id)
            .then(() => {
                // If the deleted project was the one the menu bar save button
                // was updating, forget the link: the next save must create a
                // fresh project instead of updating something that is gone.
                if (String(project.id) === String(this.props.linkedProjectId)) {
                    this.props.onProjectUnlinked();
                }
                this.setState({
                    busyProjectId: null,
                    notice: this.props.intl.formatMessage(messages.removed, {name: project.name})
                });
                return this.loadProjects();
            })
            .catch(error => {
                this.setState({
                    busyProjectId: null,
                    error: this.props.intl.formatMessage(describeAuthError(error))
                });
            });
    }

    // React's lint rules reject arrow functions in JSX props, and creating a
    // closure per row per render is wasteful anyway. These factories hand back
    // a stable handler for a given project.
    handleOpenProjectFor (project) {
        return () => this.handleOpenProject(project);
    }

    handleAskDeleteFor (project) {
        return () => this.handleAskDelete(project);
    }

    render () {
        const {isLoading, busyProjectId, renamingId} = this.state;
        return (
            <Modal
                className={styles.modalContent}
                id="my-projects-modal"
                onRequestClose={this.props.onRequestClose}
            >
                <Box
                    className={styles.body}
                    id="my-projects-dialog"
                >
                    <h2 className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </h2>

                    <h3 className={styles.listTitle}>
                        <FormattedMessage {...messages.savedList} />
                    </h3>

                    {isLoading ? (
                        <div className={styles.hint}>
                            <FormattedMessage {...messages.loading} />
                        </div>
                    ) : null}

                    {!isLoading && this.state.projects.length === 0 ? (
                        <div className={styles.hint}>
                            <FormattedMessage {...messages.empty} />
                        </div>
                    ) : null}

                    {!isLoading && this.state.projects.length > 0 ? (
                        <ul className={styles.list}>
                            {this.state.projects.map(project => {
                                const isRenaming = renamingId === project.id;
                                const isBusy = busyProjectId === project.id;
                                return (
                                    <li
                                        className={styles.listItem}
                                        key={project.id}
                                    >
                                        {isRenaming ? (
                                            <input
                                                autoFocus
                                                className={styles.renameInput}
                                                disabled={isBusy}
                                                id="my-projects-rename-input"
                                                maxLength={MAX_NAME_LENGTH}
                                                placeholder={this.props.intl.formatMessage(
                                                    messages.renamePlaceholder
                                                )}
                                                type="text"
                                                value={this.state.renameValue}
                                                onChange={this.handleRenameChange}
                                                onKeyDown={this.handleRenameKeyDown}
                                            />
                                        ) : (
                                            <div className={styles.itemInfo}>
                                                <span className={styles.itemName}>{project.name}</span>
                                                <span className={styles.itemMeta}>
                                                    {`${formatTime(project.updated_at)} · ${formatBytes(project.size)}`}
                                                </span>
                                            </div>
                                        )}
                                        <div className={styles.itemActions}>
                                            {isRenaming ? (
                                                <React.Fragment>
                                                    <button
                                                        className={styles.primaryButton}
                                                        disabled={isBusy}
                                                        id="my-projects-rename-confirm"
                                                        type="button"
                                                        onClick={this.handleConfirmRename}
                                                    >
                                                        <FormattedMessage {...messages.renameConfirm} />
                                                    </button>
                                                    <button
                                                        className={styles.secondaryButton}
                                                        disabled={isBusy}
                                                        id="my-projects-rename-cancel"
                                                        type="button"
                                                        onClick={this.handleCancelRename}
                                                    >
                                                        <FormattedMessage {...messages.renameCancel} />
                                                    </button>
                                                </React.Fragment>
                                            ) : (
                                                <React.Fragment>
                                                    <button
                                                        className={styles.secondaryButton}
                                                        disabled={isBusy}
                                                        id={`my-projects-rename-${project.id}`}
                                                        type="button"
                                                        onClick={this.handleStartRenameFor(project)}
                                                    >
                                                        <FormattedMessage {...messages.rename} />
                                                    </button>
                                                    <button
                                                        className={styles.secondaryButton}
                                                        disabled={isBusy}
                                                        id={`my-projects-open-${project.id}`}
                                                        type="button"
                                                        onClick={this.handleOpenProjectFor(project)}
                                                    >
                                                        <FormattedMessage {...messages.open} />
                                                    </button>
                                                    <button
                                                        className={styles.dangerButton}
                                                        disabled={isBusy}
                                                        id={`my-projects-delete-${project.id}`}
                                                        type="button"
                                                        onClick={this.handleAskDeleteFor(project)}
                                                    >
                                                        <FormattedMessage {...messages.remove} />
                                                    </button>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : null}

                    {this.state.error ? (
                        <div
                            className={styles.error}
                            role="alert"
                        >
                            {this.state.error}
                        </div>
                    ) : null}
                    {this.state.notice ? (
                        <div
                            className={styles.notice}
                            role="status"
                        >
                            {this.state.notice}
                        </div>
                    ) : null}
                    {this.state.pendingDelete ? (
                        <div
                            className={styles.confirmBox}
                            role="alertdialog"
                        >
                            <span className={styles.confirmText}>
                                <FormattedMessage
                                    {...messages.confirmRemove}
                                    values={{name: this.state.pendingDelete.name}}
                                />
                            </span>
                            <div className={styles.confirmActions}>
                                <button
                                    className={styles.dangerButton}
                                    id="my-projects-confirm-delete"
                                    type="button"
                                    onClick={this.handleConfirmDelete}
                                >
                                    <FormattedMessage {...messages.remove} />
                                </button>
                                <button
                                    className={styles.secondaryButton}
                                    id="my-projects-cancel-delete"
                                    type="button"
                                    onClick={this.handleCancelDelete}
                                >
                                    <FormattedMessage {...messages.cancel} />
                                </button>
                            </div>
                        </div>
                    ) : null}
                </Box>
            </Modal>
        );
    }
}

MyProjects.propTypes = {
    didProjectLoad: PropTypes.func,
    intl: intlShape.isRequired,
    linkedProjectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    loadProject: PropTypes.func.isRequired,
    onProjectOpened: PropTypes.func,
    onProjectRenamed: PropTypes.func,
    onProjectUnlinked: PropTypes.func,
    onRequestClose: PropTypes.func.isRequired
};

export default injectIntl(MyProjects);
export {MyProjects};
